import React, {Fragment} from 'react';

import {Box, Divider, Stack, Typography, useTheme} from '@mui/material';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {Loading} from 'presentation/components/Loading/Loading';
import {ModalError} from 'presentation/components/ModalError';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {Attachment} from 'domain/usecases';
import {CustomStyledCardContainer, IconButtonStyled, StyledFileName} from './styles';
import {UploadFiles} from './UploadFiles';
import {LoadingState} from '../LoadingState';
import {useRequestAuthorization} from '../../../hooks';
import {AttachmentsProps} from './types';
import {useRecoverFileHandler} from '../../../hooks/useRecoverFileHandler';

const {title} = translations['pt-br'].privateFlow.requestAuthorizations.attachments;
const {tryAgain, goToHome} = translations['pt-br'].commons;

const {
	description,
	headerTitle,
	title: titleModal,
} = translations['pt-br'].privateFlow.requestAuthorizations.errorModal;

export const Attachments = ({files, nuControle, loading, showUpload}: AttachmentsProps) => {
	const {isMobile} = useMediaQueries();
	const theme = useTheme();

	const {getRecoverFileUrl, isFetchingRecoverFile, nuFileState} = useRecoverFileHandler();

	const {errorState, setErrorState, handleCloseModalError} = useRequestAuthorization();

	const hasAttachments = !loading && files;

	const handleOpenFile = (file: Attachment) => {
		getRecoverFileUrl({
			nmArquivo: file.nmArquivo,
			nuControle,
			nuArquivo: file.nuArquivo,
			dtUpload: file.dtUpload,
		});
	};

	return (
		<CustomStyledCardContainer>
			<Typography fontWeight={600} variant="subtitle2" color="text.secondary">
				{title}
			</Typography>

			{loading ? <LoadingState /> : null}

			{hasAttachments ? (
				<Fragment>
					{files.map((file, index) => (
						<Stack key={file.nuArquivo} gap={2}>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								gap={1}>
								<StyledFileName variant="body1">{file.nmArquivo}</StyledFileName>
								<Stack direction="row" gap={1}>
									<IconButtonStyled
										data-tag-id={AutoSolEnum.AS_CLICK_ABRIR_ANEXO}
										aria-label="open"
										onClick={() => handleOpenFile(file)}
										size="large"
										disableRipple
										color="primary">
										{isFetchingRecoverFile && nuFileState === file.nuArquivo ? (
											<Box width={24}>
												<Loading
													sx={{color: theme.palette.action.disabled}}
													size={24}
												/>
											</Box>
										) : (
											<Icon name="ben-export" />
										)}
									</IconButtonStyled>
								</Stack>
							</Stack>
							{index < files.length - 1 ? <Divider /> : null}
							<ModalError
								open={errorState && nuFileState === file.nuArquivo}
								onClose={() => setErrorState(false)}
								headerTitle={headerTitle}
								title={titleModal}
								description={description}
								firstButton={{
									title: tryAgain,
									handleClick: () => {
										handleCloseModalError(false);
										handleOpenFile(file);
									},
								}}
								secondButton={{
									title: goToHome,
									handleClick: () => handleCloseModalError(true),
								}}
							/>
						</Stack>
					))}
					{showUpload ? (
						<Fragment>
							<Divider />
							<Stack gap={isMobile ? 2 : 1} sx={{borderRadius: isMobile ? 0 : 1}}>
								<UploadFiles nuControle={nuControle} />
							</Stack>
						</Fragment>
					) : null}
				</Fragment>
			) : null}
		</CustomStyledCardContainer>
	);
};
